import { logger } from "@platform/browser-logger";
import { Button } from "@platform/design-system";
import { useEffect, useRef } from "react";

import envVariables from "../projectEnvVariables";

import useToastNotificationService from "./useToastNotificationService";

const buildVersion = envVariables.VITE_REACT_APP_VERSION as string;
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes
const MAX_RETRIES = 3;

async function fetchCurrentVersion(): Promise<string | undefined> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }

    const data = (await res.json()) as { version: string };
    return data.version;
  } catch (err) {
    logger.error("Error fetching current version", err);
    throw err;
  }
}

function refreshWindow(): void {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}

/**
 * Hook that fetches the latest portal build version and compares it to the user's current version.
 * If they differ, it prompts the user to reload the application.
 */
export default function useNewVersionPrompt({
  isEnabled,
}: {
  isEnabled: boolean;
}): void {
  const { createToastNotification } = useToastNotificationService();
  const hasShownNotification = useRef(false);
  const numRetries = useRef(0);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const checkForNewVersion = async (): Promise<void> => {
      // Stop checking if we've had too many consecutive failures
      if (numRetries.current >= MAX_RETRIES) {
        logger.warn(
          `Version check disabled after ${MAX_RETRIES} consecutive failures`,
        );
        return;
      }

      try {
        const currentVersion = await fetchCurrentVersion();

        // Reset retry count on successful fetch
        numRetries.current = 0;

        const versionsExist = currentVersion && buildVersion;

        if (
          versionsExist &&
          currentVersion !== buildVersion &&
          !hasShownNotification.current
        ) {
          hasShownNotification.current = true;

          createToastNotification({
            severity: "info",
            title: "New version available",
            message: (
              <>
                A new version of the portal is available.
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={refreshWindow}
                >
                  Reload
                </Button>
              </>
            ),
          });
        }
      } catch (err) {
        numRetries.current += 1;
        logger.error(
          `Error checking for new version (attempt ${numRetries.current}/${MAX_RETRIES})`,
          err,
        );
      }
    };

    const interval = setInterval(checkForNewVersion, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [createToastNotification, isEnabled]);
}
